import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { apiFetch } from "../../utils/api";
import { useDarkMode } from "../../hooks/useDarkMode";

export default function ReadyScreenSettings() {
    const { sublocationId } = useParams();
    const { isDark, toggleDarkMode } = useDarkMode("readyScreenDarkMode");
    const [retentionSec, setRetentionSec] = useState(600);
    const [visibleColumns, setVisibleColumns] = useState({
        received: true,
        inProgress: true,
        completed: true,
    });

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const presets = useMemo(
        () => [
            { label: "2 minutes", value: 120 },
            { label: "5 minutes", value: 300 },
            { label: "10 minutes", value: 600 },
            { label: "15 minutes", value: 900 },
            { label: "30 minutes", value: 1800 },
        ],
        []
    );

    const [dark, setDark] = useState(isDark);

    const settingsQuery = useMemo(() => {
        const params = new URLSearchParams();
        if (sublocationId) params.set("sublocation_id", String(sublocationId));
        const query = params.toString();
        return query ? `?${query}` : "";
    }, [sublocationId]);

    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                setError("");
                const res = await apiFetch(`/settings/ready_screen${settingsQuery}`);
                const data = await (typeof res.json === "function" ? res.json() : res);

                const fetchedDark = !!data?.isDark;
                const fetchedRetention = Number.isFinite(Number(data?.completedRetentionSec))
                    ? Math.max(10, Math.round(Number(data.completedRetentionSec)))
                    : 600;

                if (fetchedDark !== isDark) toggleDarkMode();
                setDark(fetchedDark);
                setRetentionSec(fetchedRetention);
                setVisibleColumns({
                    received: data?.showReceived !== false,
                    inProgress: data?.showInProgress !== false,
                    completed: data?.showCompleted !== false,
                });
            } catch (e) {
                console.error("Failed to fetch Ready Screen settings:", e);
                setError(e?.message || "Failed to load settings.");
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settingsQuery]);

    const handleToggleDark = () => {
        setDark((prev) => !prev);
        toggleDarkMode();
    };

    const toggleColumn = (key) => {
        setVisibleColumns((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const handleSave = async () => {
        setSaving(true);
        setSuccess("");
        setError("");
        try {
            const payload = {
                isDark: !!dark,
                completedRetentionSec: Number.isFinite(retentionSec)
                    ? Math.max(10, Math.round(retentionSec))
                    : 600,
                showReceived: visibleColumns.received,
                showInProgress: visibleColumns.inProgress,
                showCompleted: visibleColumns.completed,
            };

            const res = await apiFetch(`/settings/ready_screen${settingsQuery}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            if (!res.ok && typeof res.status !== "undefined") {
                throw new Error(`Save failed: HTTP ${res.status}`);
            }

            setSuccess("Settings saved!");
            setTimeout(() => setSuccess(""), 2500);
        } catch (e) {
            console.error("Failed to save Ready Screen settings:", e);
            setError(e?.message || "Error saving setting.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="min-h-screen w-full bg-zinc-100 text-zinc-900 dark:bg-zinc-900 dark:text-zinc-100 p-6 transition-colors">
            <div className="max-w-3xl mx-auto">
                <header className="flex items-center justify-between mb-8">
                    <h1 className="text-4xl md:text-5xl font-bold tracking-tight">Ready Screen Settings</h1>
                    <button
                        onClick={handleSave}
                        disabled={saving || loading}
                        className="px-4 py-2 rounded-xl text-sm font-medium bg-emerald-600 text-white disabled:opacity-60 shadow hover:shadow-md transition-shadow"
                    >
                        {saving ? "Saving..." : "Save"}
                    </button>
                </header>

                {loading ? (
                    <div className="text-zinc-500 dark:text-zinc-400">Loading settings...</div>
                ) : (
                    <div className="space-y-6">
                        <section className="bg-white/70 dark:bg-zinc-800/70 border border-zinc-300 dark:border-zinc-700 rounded-2xl p-5">
                            <div className="flex items-center justify-between gap-6">
                                <div>
                                    <h2 className="text-xl font-semibold">Dark Mode</h2>
                                    <p className="text-sm text-zinc-600 dark:text-zinc-400">
                                        Toggle the interface theme for ReadyScreen.
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={handleToggleDark}
                                    className={[
                                        "relative inline-flex h-7 w-12 items-center rounded-full transition-colors",
                                        dark ? "bg-emerald-600" : "bg-zinc-300",
                                    ].join(" ")}
                                    aria-pressed={dark}
                                    aria-label="Toggle dark mode"
                                >
                                    <span
                                        className={[
                                            "inline-block h-6 w-6 transform rounded-full bg-white shadow transition-transform",
                                            dark ? "translate-x-6" : "translate-x-1",
                                        ].join(" ")}
                                    />
                                </button>
                            </div>
                        </section>

                        <section className="bg-white/70 dark:bg-zinc-800/70 border border-zinc-300 dark:border-zinc-700 rounded-2xl p-5">
                            <div className="flex flex-col gap-3">
                                <h2 className="text-xl font-semibold">Completed Ticket Retention</h2>
                                <p className="text-sm text-zinc-600 dark:text-zinc-400">
                                    How long completed tickets stay visible (in seconds).
                                </p>

                                <div className="flex items-center gap-3">
                                    <input
                                        id="retention-input"
                                        type="number"
                                        min={10}
                                        step={10}
                                        inputMode="numeric"
                                        value={retentionSec}
                                        onChange={(e) => setRetentionSec(Number(e.target.value))}
                                        className="w-40 px-3 py-2 rounded-xl bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                    />
                                    <div className="flex flex-wrap gap-2">
                                        {presets.map((p) => (
                                            <button
                                                key={p.value}
                                                type="button"
                                                onClick={() => setRetentionSec(p.value)}
                                                className={[
                                                    "px-3 py-1.5 rounded-lg text-sm border transition",
                                                    retentionSec === p.value
                                                        ? "bg-emerald-600 text-white border-emerald-600"
                                                        : "bg-white dark:bg-zinc-900 border-zinc-300 dark:border-zinc-700 text-zinc-800 dark:text-zinc-200 hover:border-emerald-400",
                                                ].join(" ")}
                                            >
                                                {p.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </section>

                        <section className="bg-white/70 dark:bg-zinc-800/70 border border-zinc-300 dark:border-zinc-700 rounded-2xl p-5">
                            <div className="flex flex-col gap-3">
                                <div>
                                    <h2 className="text-xl font-semibold">Visible Columns</h2>
                                    <p className="text-sm text-zinc-600 dark:text-zinc-400">
                                        Choose which status columns appear on this Ready Screen.
                                    </p>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                    {[
                                        { key: "received", label: "Received" },
                                        { key: "inProgress", label: "In Progress" },
                                        { key: "completed", label: "Completed" },
                                    ].map((column) => (
                                        <button
                                            key={column.key}
                                            type="button"
                                            onClick={() => toggleColumn(column.key)}
                                            className={[
                                                "flex items-center justify-between rounded-xl border px-4 py-3 text-left transition",
                                                visibleColumns[column.key]
                                                    ? "border-emerald-600 bg-emerald-600 text-white"
                                                    : "border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-zinc-800 dark:text-zinc-200",
                                            ].join(" ")}
                                        >
                                            <span className="font-medium">{column.label}</span>
                                            <span className="text-xs uppercase tracking-wide">
                                                {visibleColumns[column.key] ? "Shown" : "Hidden"}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </section>

                        {(error || success) && (
                            <div className="pt-2">
                                {error && <div className="text-sm text-red-600 dark:text-red-400">{error}</div>}
                                {success && (
                                    <div className="text-sm text-emerald-600 dark:text-emerald-400">{success}</div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
